import { useCallback, useEffect, useMemo, useState } from "react";
import userServices from "../../api/services/userService";
import filterService from "../../api/services/filterService";
import { countries } from "../../data/countries";
import { buildFilterPayload } from "./buildFilterPayload";
import { getFilterLabel as getFilterLabelUtil } from "./getFilterLabel";
import { INITIAL_FILTERS, BACKEND_LIMIT } from "./constants";

export function useUserProfiles() {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [backendPage, setBackendPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [maritalStatus, setMaritalStatus] = useState([]);
  const [education, setEducation] = useState([]);
  const [religions, setReligion] = useState([]);
  const [occupation, setOccupation] = useState([]);

  const fetchFilteredUsers = useCallback(async (filtersPayload, page) => {
    try {
      setIsLoading(true);
      const payload = buildFilterPayload(
        filtersPayload,
        page,
        BACKEND_LIMIT
      );
      const result = await userServices.getFilteredUsersAdmin(payload);
      setUsers(result?.users || []);
      setTotal(result?.total || 0);
    } catch (error) {
      console.error("Error fetching filtered users:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      const data = await filterService.getAllFilters();
      setMaritalStatus(data.maritalStatus || []);
      setEducation(data.educations || []);
      setOccupation(data.occupations || []);
      setReligion(data.religions || []);
    };
    load();
  }, []);

  // Fetch whenever filters or page change (instant API call, no Apply needed)
  useEffect(() => {
    fetchFilteredUsers(filters, backendPage);
  }, [
    backendPage,
    filters.occupation,
    filters.education,
    filters.marital,
    filters.religion,
    filters.minHeight,
    filters.maxHeight,
    filters.minAge,
    filters.maxAge,
    filters.radius,
    filters.latitude,
    filters.longitude,
    filters.country,
    filters.sort,
    filters.is_web,
    filters.userId,
    filters.email,
    filters.contactNo,
    filters.contactMissing,
    filters.gender,
    filters.created_date_start,
    filters.created_date_end,
    filters.premium,
    filters.profileCompletion,
    fetchFilteredUsers,
  ]);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "contactMissing" && value === "yes") next.contactNo = "";
      if (name === "contactNo" && String(value).trim() !== "") {
        if (next.contactMissing === "yes") next.contactMissing = "";
      }
      return next;
    });
    setBackendPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setBackendPage(1);
  }, []);

  const removeFilter = useCallback((key) => {
    setFilters((prev) => ({ ...prev, [key]: "" }));
    setBackendPage(1);
  }, []);

  const getFilterLabel = useCallback(
    (key, value) =>
      getFilterLabelUtil(key, value, {
        occupation,
        education,
        maritalStatus,
        religions,
      }),
    [occupation, education, maritalStatus, religions]
  );

  const getActiveFiltersCount = useCallback(() => {
    return Object.values(filters).filter(
      (v) => v !== "" && v != null && v !== undefined
    ).length;
  }, [filters]);

  const activeFilterEntries = useMemo(
    () =>
      Object.keys(filters).filter(
        (key) => filters[key] !== "" && filters[key] != null
      ),
    [filters]
  );

  const primaryFields = useMemo(
    () => [
      { name: "userId", label: "User ID", type: "text", placeholder: "e.g. 12345" },
      { name: "email", label: "Email", type: "text", placeholder: "Search by email" },
      { name: "contactNo", label: "Contact", type: "text", placeholder: "Phone" },
      {
        name: "contactMissing",
        label: "Phone status",
        type: "select",
        options: [
          { value: "", name: "All users" },
          { value: "yes", name: "No contact number" },
          { value: "has", name: "Has contact number" },
        ],
      },
      {
        name: "gender",
        label: "Gender",
        type: "select",
        options: [
          { value: "", name: "All" },
          { value: "male", name: "Male" },
          { value: "female", name: "Female" },
          { value: "other", name: "Other" },
        ],
      },
      {
        name: "premium",
        label: "Premium",
        type: "select",
        options: [
          { value: "", name: "All" },
          { value: "yes", name: "Yes" },
          { value: "no", name: "No" },
        ],
      },
      {
        name: "profileCompletion",
        label: "Profile %",
        type: "select",
        options: [
          { value: "", name: "All" },
          { value: "pct0", name: "0%" },
          { value: "pct1_25", name: "1% – 25%" },
          { value: "pct26_50", name: "26% – 50%" },
          { value: "gt70", name: "Greater than 70%" },
          { value: "lt70", name: "Less than 70%" },
        ],
      },
      {
        name: "is_web",
        label: "Source",
        type: "select",
        options: [
          { value: "", name: "All" },
          { value: "web", name: "Web" },
          { value: "mobile", name: "Mobile" },
        ],
      },
    ],
    []
  );

  const secondaryFields = useMemo(
    () => [
      {
        name: "occupation",
        label: "Occupation",
        type: "select",
        options: [{ value: "", name: "Select" }, ...(occupation?.map((o) => ({ value: o.id, name: o.name })) ?? [])],
      },
      {
        name: "education",
        label: "Education",
        type: "select",
        options: [{ value: "", name: "Select" }, ...(education?.map((o) => ({ value: o.id, name: o.name })) ?? [])],
      },
      {
        name: "marital",
        label: "Marital",
        type: "select",
        options: [{ value: "", name: "Select" }, ...(maritalStatus?.map((o) => ({ value: o.id, name: o.name })) ?? [])],
      },
      {
        name: "religion",
        label: "Religion",
        type: "select",
        options: [{ value: "", name: "Select" }, ...(religions?.map((o) => ({ value: o.id, name: o.name })) ?? [])],
      },
      { name: "minAge", label: "Min Age", type: "number", placeholder: "18", min: 1, max: 120 },
      { name: "maxAge", label: "Max Age", type: "number", placeholder: "99", min: 1, max: 120 },
      { name: "minHeight", label: "Min Ht", type: "number", placeholder: "cm" },
      { name: "maxHeight", label: "Max Ht", type: "number", placeholder: "cm" },
      { name: "created_date_start", label: "Created From", type: "date" },
      { name: "created_date_end", label: "Created To", type: "date" },
      {
        name: "country",
        label: "Country",
        type: "select",
        options: [{ value: "", name: "Select" }, ...(countries?.map((c) => ({ value: c.name, name: c.name })) ?? [])],
      },
      { name: "latitude", label: "Lat", type: "text", placeholder: "31.52" },
      { name: "longitude", label: "Long", type: "text", placeholder: "74.35" },
      { name: "radius", label: "Radius (m)", type: "number", placeholder: "m" },
    ],
    [occupation, education, maritalStatus, religions]
  );

  return {
    filters,
    setFilters,
    handleFilterChange,
    clearFilters,
    removeFilter,
    getFilterLabel,
    getActiveFiltersCount,
    activeFilterEntries,
    primaryFields,
    secondaryFields,
    users,
    total,
    isLoading,
    backendPage,
    setBackendPage,
    backendLimit: BACKEND_LIMIT,
    fetchFilteredUsers,
  };
}
